import { useEffect, useMemo, useState } from 'react';
import { i18n, type PalletState } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatDate, formatQty } from '../lib/format.js';
import { type SortDir, useTableSort } from '../lib/sortRows.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

const ELIGIBLE_STATES: PalletState[] = ['ARRIVED', 'PARTIALLY_DISPATCHED', 'DAMAGED'];

interface PalletOption {
  id: string;
  code: string;
  state: PalletState;
  arrivedAt: string | null;
  comment: string | null;
  locationRow: string | null;
  locationShelf: string | null;
  locationPosition: string | null;
  contentsVisibleAt: string | null;
  contents: {
    skuId: string;
    remainingQty: string;
    sku: { code: string; nameLt: string };
  }[];
  orderLinks: { kind: 'WHOLE_PALLET' | 'OUTPUT' }[];
}

const locationKey = (pallet: PalletOption) =>
  [pallet.locationRow, pallet.locationShelf, pallet.locationPosition].filter(Boolean).join('-');

const contentsKey = (pallet: PalletOption) =>
  pallet.contents.map((line) => `${line.sku.code} ${line.sku.nameLt}`).join(' ');

const PALLET_SORT = {
  code: (pallet: PalletOption) => pallet.code,
  state: (pallet: PalletOption) => pallet.state,
  location: locationKey,
  arrived: (pallet: PalletOption) => pallet.arrivedAt,
  comment: (pallet: PalletOption) => pallet.comment,
  contents: contentsKey,
} as const;

interface SortControl {
  sortKey: string;
  sortDir: SortDir;
  toggle: (key: string) => void;
}

function SortableTh({ label, col, sort }: { label: string; col: string; sort: SortControl }) {
  const active = sort.sortKey === col;
  return (
    <th className="px-3 py-2 font-medium">
      <button
        type="button"
        onClick={() => sort.toggle(col)}
        className="inline-flex items-center gap-1 hover:text-foreground"
        aria-label={label}
      >
        <span>{label}</span>
        <span className="text-[10px] text-muted-foreground">
          {active ? (sort.sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

function Contents({ pallet }: { pallet: PalletOption }) {
  if (pallet.contents.length === 0) {
    return <span className="text-muted-foreground">{t('order.pallets.contents_unknown')}</span>;
  }
  return (
    <ul className="space-y-1">
      {pallet.contents.map((line) => (
        <li key={line.skuId}>
          <span className="font-mono">{line.sku.code}</span> — {line.sku.nameLt}{' '}
          <span className="whitespace-nowrap tabular-nums">({formatQty(line.remainingQty)})</span>
        </li>
      ))}
    </ul>
  );
}

export function OrderPalletPickerDialog({
  orderId,
  existingIds,
  onClose,
  onSaved,
}: {
  orderId: string;
  existingIds: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [options, setOptions] = useState<PalletOption[] | null>(null);
  const [selected, setSelected] = useState(() => new Set(existingIds));
  const [stateFilter, setStateFilter] = useState<PalletState | ''>('');
  const [rowFilter, setRowFilter] = useState('');
  const [shelfFilter, setShelfFilter] = useState('');
  const [arrivedFrom, setArrivedFrom] = useState('');
  const [arrivedTo, setArrivedTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<PalletOption[]>(`/orders/${orderId}/pallet-options`)
      .then(setOptions)
      .catch(() => setError(t('common.error')));
  }, [orderId]);

  const filteredOptions = useMemo(() => {
    if (!options) return [];
    const row = rowFilter.trim().toUpperCase();
    const shelf = shelfFilter.trim().toUpperCase();
    return options.filter((pallet) => {
      if (stateFilter && pallet.state !== stateFilter) return false;
      if (row && (pallet.locationRow ?? '').toUpperCase() !== row) return false;
      if (shelf && (pallet.locationShelf ?? '').toUpperCase() !== shelf) return false;
      const arrived = pallet.arrivedAt?.slice(0, 10) ?? null;
      if (arrivedFrom && (!arrived || arrived < arrivedFrom)) return false;
      if (arrivedTo && (!arrived || arrived > arrivedTo)) return false;
      return true;
    });
  }, [options, stateFilter, rowFilter, shelfFilter, arrivedFrom, arrivedTo]);

  const palletSort = useTableSort(filteredOptions, PALLET_SORT, { key: 'code' });
  const hasFilters = Boolean(stateFilter || rowFilter || shelfFilter || arrivedFrom || arrivedTo);

  function toggle(palletId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(palletId)) next.delete(palletId);
      else next.add(palletId);
      return next;
    });
  }

  function clearFilters() {
    setStateFilter('');
    setRowFilter('');
    setShelfFilter('');
    setArrivedFrom('');
    setArrivedTo('');
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api(`/orders/${orderId}/pallets`, {
        method: 'PUT',
        body: { palletIds: [...selected] },
      });
      onSaved();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('order.pallets.pick_title')} onClose={onClose} size="wide" height="tall">
      <div className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">{t('order.pallets.pick_hint')}</p>
        {options === null ? (
          <div className="text-muted-foreground">{t('common.loading')}</div>
        ) : options.length === 0 ? (
          <div className="rounded border border-dashed border-border p-4 text-center text-muted-foreground">
            {t('order.pallets.none_available')}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 rounded border border-border p-3">
              <label className="flex items-center gap-2">
                <span className="text-muted-foreground">{t('warehouse.filter.state')}</span>
                <select
                  value={stateFilter}
                  onChange={(event) => setStateFilter(event.target.value as PalletState | '')}
                  className="rounded border border-border bg-background px-2 py-1"
                >
                  <option value="">{t('warehouse.filter.all_states')}</option>
                  {ELIGIBLE_STATES.map((state) => (
                    <option key={state} value={state}>
                      {t(`pallet.state.${state}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2">
                <span className="text-muted-foreground">{t('location.row')}</span>
                <input
                  value={rowFilter}
                  onChange={(event) => setRowFilter(event.target.value)}
                  maxLength={8}
                  className="w-16 rounded border border-border bg-background px-2 py-1 text-center font-mono"
                />
              </label>
              <label className="flex items-center gap-2">
                <span className="text-muted-foreground">{t('location.shelf')}</span>
                <input
                  value={shelfFilter}
                  onChange={(event) => setShelfFilter(event.target.value)}
                  maxLength={8}
                  className="w-16 rounded border border-border bg-background px-2 py-1 text-center font-mono"
                />
              </label>
              <label className="flex items-center gap-2">
                <span className="text-muted-foreground">{t('warehouse.filter.arrived_from')}</span>
                <input
                  type="date"
                  value={arrivedFrom}
                  onChange={(event) => setArrivedFrom(event.target.value)}
                  className="rounded border border-border bg-background px-2 py-1"
                />
              </label>
              <label className="flex items-center gap-2">
                <span className="text-muted-foreground">{t('warehouse.filter.arrived_to')}</span>
                <input
                  type="date"
                  value={arrivedTo}
                  onChange={(event) => setArrivedTo(event.target.value)}
                  className="rounded border border-border bg-background px-2 py-1"
                />
              </label>
              {hasFilters ? (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                >
                  {t('common.clear')}
                </button>
              ) : null}
              <span className="ml-auto text-xs tabular-nums text-muted-foreground">
                {filteredOptions.length} / {options.length}
              </span>
            </div>

            {filteredOptions.length === 0 ? (
              <div className="rounded border border-dashed border-border p-4 text-center text-muted-foreground">
                {t('order.pallets.none_matching')}
              </div>
            ) : (
              <>
                <div className="hidden overflow-x-auto rounded border border-border md:block">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="w-10 px-3 py-2 font-medium">{t('order.pallets.select')}</th>
                        <SortableTh label={t('pallet.col.code')} col="code" sort={palletSort} />
                        <SortableTh label={t('pallet.col.state')} col="state" sort={palletSort} />
                        <SortableTh label={t('location.label')} col="location" sort={palletSort} />
                        <SortableTh
                          label={t('pallet.col.arrived')}
                          col="arrived"
                          sort={palletSort}
                        />
                        <SortableTh
                          label={t('pallet.col.comment')}
                          col="comment"
                          sort={palletSort}
                        />
                        <SortableTh
                          label={t('pallet.contents.title')}
                          col="contents"
                          sort={palletSort}
                        />
                      </tr>
                    </thead>
                    <tbody>
                      {palletSort.sorted.map((pallet) => (
                        <tr key={pallet.id} className="border-t border-border align-top">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selected.has(pallet.id)}
                              onChange={() => toggle(pallet.id)}
                              aria-label={`${t('order.pallets.select')} ${pallet.code}`}
                            />
                          </td>
                          <td className="px-3 py-2 font-mono font-medium">{pallet.code}</td>
                          <td className="px-3 py-2">{t(`pallet.state.${pallet.state}`)}</td>
                          <td className="px-3 py-2 font-mono text-xs">
                            {locationKey(pallet) || '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                            {formatDate(pallet.arrivedAt)}
                          </td>
                          <td className="max-w-48 px-3 py-2 text-xs text-muted-foreground">
                            {pallet.comment || '—'}
                          </td>
                          <td className="min-w-56 px-3 py-2 text-xs">
                            <Contents pallet={pallet} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className="space-y-2 md:hidden">
                  {palletSort.sorted.map((pallet) => (
                    <li key={pallet.id}>
                      <label className="flex cursor-pointer gap-3 rounded border border-border p-3 hover:bg-muted/50">
                        <input
                          type="checkbox"
                          checked={selected.has(pallet.id)}
                          onChange={() => toggle(pallet.id)}
                          aria-label={`${t('order.pallets.select')} ${pallet.code}`}
                          className="mt-1"
                        />
                        <span className="min-w-0 flex-1 space-y-2">
                          <span className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-mono font-medium">{pallet.code}</span>
                            <span className="text-xs">{t(`pallet.state.${pallet.state}`)}</span>
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {t('location.label')}: {locationKey(pallet) || '—'} ·{' '}
                            {t('pallet.col.arrived')}: {formatDate(pallet.arrivedAt)}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {t('pallet.col.comment')}: {pallet.comment || '—'}
                          </span>
                          <span className="block text-xs">
                            <Contents pallet={pallet} />
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
        {error ? <div className="text-red-600">{error}</div> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy || options === null}
            className="rounded bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
